// Attachment upload pipeline (INT-04, CRY-04, T-FILE_UPLOAD).
//
// Pipeline: strip EXIF → encrypt bytes with DEK + AAD=caseId → request
// presigned URL → PUT ciphertext. The Worker SHA-256s the ciphertext to derive
// the R2 key (content-addressable, anonymity-preserving) and registers the
// AttachmentRef on the denúncia record.
//
// Anonymity rationale:
//   - EXIF strip happens BEFORE encrypt so location/device tags never even hit
//     the ciphertext (defense-in-depth).
//   - The PUT goes through the Worker proxy (D-26 simplified). The browser
//     never sees R2 credentials; the Worker re-validates mime/size/nonce on
//     upload. CRY-04 binding (AAD=caseId) is enforced client-side at encrypt.
//   - The nonce echoed back in AttachmentRef.nonce is the AES-GCM IV, so the
//     denunciante can later prove decrypt parity if the case is reopened.

import { encryptBytes } from '@aions/crypto';
import { stripExif } from './exif-strip';
import { apiUrl } from './api-base';
import { bytesToBase64 } from '../utils/bytes';

export interface AttachmentRefShape {
  /** SHA-256 hex of the ciphertext, computed by the Worker (R2 object key). */
  contentHash: string;
  /** Base64-encoded 12-byte AES-GCM IV used to encrypt this attachment. */
  nonce: string;
  /** Coarse size bucket for the audit log (no exact byte count leaked). */
  sizeBucket: '<1MB' | '1-5MB' | '5-25MB';
}

interface SignUrlResponse {
  uploadUrl: string;
  expiresAt: number;
  method?: 'PUT';
}

interface UploadResponse {
  ok: boolean;
  contentHash: string;
}

const SIGN_URL_PATH = '/api/denuncia/attachments/sign-url';

/**
 * Strips EXIF, encrypts with the per-denúncia DEK, requests a presigned URL,
 * and PUTs the ciphertext. Returns the AttachmentRef-shaped result the wizard
 * appends to the denúncia submission body.
 *
 * Throws if any step of the pipeline fails. The wizard surfaces the failure
 * to the user; partial attachments are not silently dropped.
 */
export async function uploadAttachment(
  file: File,
  dekRaw: Uint8Array,
  caseId: string,
): Promise<AttachmentRefShape> {
  const cleaned = await stripExif(file);
  const plaintext = new Uint8Array(await cleaned.arrayBuffer());

  const { ciphertext, nonce } = await encryptBytes(plaintext, dekRaw, caseId);
  const nonceB64 = bytesToBase64(nonce);

  const signResp = await fetch(apiUrl(SIGN_URL_PATH), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      caseId,
      contentType: cleaned.type,
      contentLength: ciphertext.byteLength,
      nonce: nonceB64,
    }),
  });
  if (!signResp.ok) {
    throw new Error(`attachments-upload: sign-url failed (${String(signResp.status)})`);
  }
  const sign = (await signResp.json()) as SignUrlResponse;
  if (!sign.uploadUrl) {
    throw new Error('attachments-upload: sign-url response missing uploadUrl');
  }

  const putResp = await fetch(apiUrl(sign.uploadUrl), {
    method: 'PUT',
    headers: { 'content-type': cleaned.type },
    body: bytesToArrayBuffer(ciphertext),
  });
  if (!putResp.ok) {
    throw new Error(`attachments-upload: upload PUT failed (${String(putResp.status)})`);
  }
  const upload = (await putResp.json()) as UploadResponse;

  return {
    contentHash: upload.contentHash,
    nonce: nonceB64,
    sizeBucket: bucketFor(ciphertext.byteLength),
  };
}

function bucketFor(bytes: number): AttachmentRefShape['sizeBucket'] {
  if (bytes < 1_000_000) return '<1MB';
  if (bytes < 5_000_000) return '1-5MB';
  return '5-25MB';
}

function bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
